import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { MITAKA_XZ, type KanjinSite } from '../kanjinData'

/** ⑤ 大圏弧＋ニアミス — 木片が放物弧で三鷹へ。2片がカメラすれすれを風切り音つきで通過 */
export class Proto5 extends ProtoBase {
  readonly title = '5. 大圏弧＋ニアミス'
  readonly desc = '全国から木片が弧を描いて三鷹へ。うち2片が視点のすぐ横を掠める'

  private glow!: THREE.Sprite
  private arrived = 0
  private nearChipIdx: number[] = []
  private whooshed = new Set<number>()
  private waveT = 0
  private tmp = new THREE.Vector3()

  protected start(): void {
    this.ctx.setWorld('map')
    this.ctx.setCaption('六十六人の勧進——木片が、三鷹をめざす')

    // 三鷹の収束グロー
    const tex = Proto5.makeGlowTexture()
    this.glow = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }),
    )
    this.glow.position.set(MITAKA_XZ.x, 1.5, MITAKA_XZ.z)
    this.glow.scale.setScalar(2)
    this.group.add(this.glow)

    this.launchAll()
  }

  private launchAll(): void {
    this.arrived = 0
    this.whooshed.clear()
    this.nearChipIdx = []
    const target = new THREE.Vector3(MITAKA_XZ.x, 1.2, MITAKA_XZ.z)
    const starts: { pos: THREE.Vector3; site: KanjinSite }[] = []
    this.ctx.markers.forEach(({ site, pos }) => starts.push({ pos: pos.clone().setY(pos.y + 2.6), site }))
    this.ctx.chips.launchWave(starts, target, { stagger: 0.4, dur: 5, apexMin: 16, apexMax: 32 })

    // ニアミス2片: カメラ前方を経由点にする
    const cam = this.ctx.camera
    const fwd = new THREE.Vector3()
    cam.getWorldDirection(fwd)
    for (let k = 0; k < 2; k++) {
      const via = cam.position
        .clone()
        .addScaledVector(fwd, 14 + k * 6)
        .add(new THREE.Vector3((k ? -1 : 1) * 4, -3 + k * 5, 0))
      const s = starts[k * 5 % starts.length]
      const idx = this.ctx.chips.launch({
        from: s.pos.clone(),
        to: target.clone(),
        via,
        dur: 4.2,
        delay: 2.5 + k * 2.8,
        site: s.site,
        scale: 1.6,
      })
      if (idx >= 0) this.nearChipIdx.push(idx)
    }

    this.ctx.chips.onArrive = (site) => {
      this.arrived++
      this.ctx.sfx.play('chime', this.glow.position)
      this.ctx.setCaption(`三鷹へ ${this.arrived} / 14 ${site ? `——「${site.piece}」到着` : ''}`)
    }
  }

  update(dt: number): void {
    this.elapsed += dt
    // ニアミス検知→風切り
    for (const idx of this.nearChipIdx) {
      if (this.whooshed.has(idx)) continue
      if (this.ctx.chips.positionOf(idx, this.tmp)) {
        if (this.tmp.distanceTo(this.ctx.camera.position) < 13) {
          this.whooshed.add(idx)
          this.ctx.sfx.play('whoosh', this.tmp)
        }
      }
    }
    // グロー成長・脈動
    const s = 2 + this.arrived * 0.9
    this.glow.scale.setScalar(s * (1 + 0.06 * Math.sin(this.elapsed * 5)))

    // 全片到着でループ
    if (this.arrived >= 14 && this.waveT === 0) {
      this.waveT = this.elapsed
      this.ctx.setCaption('全国の木片が、一畳に集まった——')
    }
    if (this.waveT > 0 && this.elapsed - this.waveT > 4) {
      this.waveT = 0
      this.ctx.chips.clear()
      this.launchAll()
    }
  }

  protected onDispose(): void {
    this.ctx.chips.onArrive = undefined
    this.ctx.chips.clear()
  }

  private static makeGlowTexture(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
    g.addColorStop(0, 'rgba(255,225,150,1)')
    g.addColorStop(0.4, 'rgba(255,190,90,0.5)')
    g.addColorStop(1, 'rgba(255,180,80,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 128, 128)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }
}
